import React from 'react'
import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Spinner, Button, Nav, Tab, Row, Col, Form } from 'react-bootstrap';
import useAxiosPrivate from '../../context/hooks/useAxiosPrivate';
import PermissionWrapper from '../../context/PermissionWrapper';
import '../../appstyles/TrackingCSS/OrderTracking.css'

const requiredField = yup.string().required('Este campo es requerido');

const stepSchemas = {
1: yup.object().shape({
  supplier_consecutive: requiredField,
  freight_type: requiredField,
}),
2: yup.object().shape({
  initial_range_etd: yup.date().nullable().typeError('Debe ingresar una fecha').required('Este campo es requerido'),
  final_range_etd: yup.date().nullable().typeError('Debe ingresar una fecha').required('Este campo es requerido'),
}),
3: yup.object().shape({
  confirmation_etd: yup.date().nullable().typeError('Debe ingresar una fecha').required('Este campo es requerido'),
  carrier_name: requiredField,
}),
4: yup.object().shape({
  bl_number: requiredField,
  bl_date: yup.date().nullable().typeError('Debe ingresar una fecha').required('Este campo es requerido'),
  eta: yup.date().nullable().typeError('Debe ingresar una fecha').required('Este campo es requerido'),
}),
5: yup.object().shape({
  approved_documents: yup.boolean().default(false),
}),
6: yup.object().shape({
  bl_in_destination: yup.boolean().default(false),
  container_days: yup.number().typeError('Debe ser un número').required('Este campo es requerido').positive().integer(),
  original_documents_date: yup.date().nullable().typeError('Debe ingresar una fecha').required('Este campo es requerido'),
}),
7: yup.object().shape({
  ata: yup.date().nullable().typeError('Debe ingresar una fecha').required('Este campo es requerido'),
}),
};

function OrderTracking() {
  const axiosInstance = useAxiosPrivate();
  const { trackId } = useParams();
  const [tracking, setTracking] = useState(null);
  const [editingStep, setEditingStep] = useState(null);
  const [currentStep, setCurrentStep] = useState(1); //establece los endpoints
  const [activeKey, setActiveKey] = useState("Pendiente"); //para el tab
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: yupResolver(stepSchemas[currentStep]),
    defaultValues: {
      freight_type: 'Marítimo',
    }
    });

    const fetchTrackingData = async () => {
      try {
        const response = await axiosInstance.get(`api/logistics-tracking/${trackId}/`);
        setTracking(response.data);
        const trackingStatus = response.data.status;
        const normalizedStatus = 
          trackingStatus === "Documentos originales entregados" || trackingStatus === "Cerrado"
            ? "final"
            : trackingStatus;
    
        setActiveKey(normalizedStatus);
        setCurrentStep(stepMap[normalizedStatus]);
      } catch (error) {
        console.error('API request failed:', error);
      }
    };

    // Fetch tracking details
    useEffect(() => {
      fetchTrackingData();
      // eslint-disable-next-line
    }, [trackId, axiosInstance]); 

    //format date
    const formatDate = (date) => {
      const d = new Date(date);
      const month = `${d.getMonth() + 1}`.padStart(2, '0');
      const day = `${d.getDate()}`.padStart(2, '0');
      const year = d.getFullYear();
      return `${year}-${month}-${day}`;
    };

    //Deadline status
    const getDeadlineStatus = (status) => {
      switch (status) {
        case 'A tiempo':
          return {
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="green" className="bi bi-check-circle" viewBox="0 0 16 16">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                <path d="m10.97 4.97-.02.022-3.473 4.425-2.093-2.094a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05"/>
              </svg>
            ),
            color: 'green',
            backgroundColor: '#e2f8d4'
          };
        case 'Advertencia':
          return {
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="orange" className="bi bi-exclamation-triangle" viewBox="0 0 16 16">
                <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.15.15 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.2.2 0 0 1-.054.06.1.1 0 0 1-.066.017H1.146a.1.1 0 0 1-.066-.017.2.2 0 0 1-.054-.06.18.18 0 0 1 .002-.183L7.884 2.073a.15.15 0 0 1 .054-.057m1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767z"/>
                <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z"/>
              </svg>
            ),
            color: 'orange',
            backgroundColor: '#f7eeca'
          };
        case 'Retraso':
          return {
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="red" className="bi bi-x-circle" viewBox="0 0 16 16">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
              </svg>
            ),
            color: 'red',
            backgroundColor: '#f7d3ca'
          };
        default:
          return {
            icon: null,
            color: 'black'
          };
      }
    };
  
    if (!tracking) {
      return(
      <div className='loading-container'>
        <p style={{fontFamily: 'Roboto, sans-serif'}}>Cargando...</p>
        <Spinner animation="border" variant="info" style={{width: '4.5rem', height: '4.5rem'}} />
      </div>
      );
    }
    
    const stepMap = {
        'Pendiente': 1,
        'Asignado': 2,
        'Firmado': 3,
        'Confirmado': 4,
        'BL recibido': 5,
        'Documentos aprobados': 6,
        'final': 7,
    };

    const stepFields = {
      2: ["supplier_consecutive", "freight_type"],
      3: ["supplier_consecutive", "freight_type", "initial_range_etd", "final_range_etd"],
      4: ["supplier_consecutive", "freight_type", "initial_range_etd", "final_range_etd", "confirmation_etd", "carrier_name"],
      5: ["supplier_consecutive", "freight_type", "initial_range_etd", "final_range_etd", "confirmation_etd", "carrier_name", "bl_number", "bl_date", "eta"],
      6: ["supplier_consecutive", "freight_type", "initial_range_etd", "final_range_etd", "confirmation_etd", "carrier_name", "bl_number", "bl_date", "eta", "approved_documents"],
      7: ["supplier_consecutive", "freight_type", "initial_range_etd", "final_range_etd", "confirmation_etd", "carrier_name", "bl_number", "bl_date", "eta", "approved_documents", "bl_in_destination", "original_documents_date", "container_days"],
    };
  
    const isStepCompleted = (step) => {
      const fields = stepFields[step];
      return fields.every(field => tracking[field] !== null && tracking[field] !== undefined && tracking[field] !== '' && tracking[field] !== false);
    };

    // Edit tracking step
    const handleEdit = (step) => {
      setEditingStep(step);
      reset(tracking);
    };

    const handleCancel = () => {
      setEditingStep(null);
    };
    
    const onSubmit = async (data) => {
      try {
        setLoading(true);
        
        // Format only the date fields present in data
        const formattedData = {
          tracking_id: trackId,
          ...Object.fromEntries(
            Object.entries(data).map(([key, value]) =>
              ["initial_range_etd", "final_range_etd", "confirmation_etd", "bl_date", "original_documents_date", "eta", "ata"].includes(key)
                ? [key, formatDate(value)]
                : [key, value]
            )
          ),
        };

        // Define the fields required per step
        const stepFields = {
          "Pendiente": ["tracking_id", "supplier_consecutive", "freight_type"],
          "Asignado": ["tracking_id", "initial_range_etd", "final_range_etd"],
          "Firmado": ["tracking_id", "confirmation_etd", "carrier_name"],
          "Confirmado": ["tracking_id", "bl_number", "bl_date", "eta"],
          "BL recibido": ["tracking_id", "approved_documents"],
          "Documentos aprobados": ["tracking_id", "bl_in_destination", "original_documents_date", "container_days"],
          "final": ["tracking_id", "ata"],
        };

        const currentFields = stepFields[editingStep];
        const filteredData = Object.fromEntries(
          Object.entries(formattedData).filter(([key]) => stepFields[editingStep]?.includes(key))
        );

        const isStepAlreadyFilled = currentFields?.every(
          (field) => tracking[field] !== null && tracking[field] !== undefined && tracking[field] !== ''
        );
    
        let endpoint = '';
        let method = '';

        if (isStepAlreadyFilled) {
          endpoint = `api/logistics-tracking/${trackId}/`;
          method = 'patch';
        } else {
        switch (editingStep) {
          case "Pendiente":
            endpoint = `api/logistics-tracking/assign/`;
            break;
          case "Asignado":
            endpoint = `api/logistics-tracking/sign/`;
            break;
          case "Firmado":
            endpoint = `api/logistics-tracking/confirm/`;
            break;
          case "Confirmado":
            endpoint = `api/logistics-tracking/bl/`;
            break;
          case "BL recibido":
            endpoint = `api/logistics-tracking/approve-documents/`;
            break;
          case "Documentos aprobados":
            endpoint = `api/logistics-tracking/originals-delivered/`; 
            break;
          case "final":
            endpoint = `api/logistics-tracking/close/`;
            break;
          default:
            throw new Error("Unknown editing step.");
        }
        method = 'post';
        }

        if (endpoint) {
          console.log("Sending data to endpoint:", endpoint, "with method:", method, "and data:", filteredData);
          const response = await axiosInstance[method](endpoint, { ...filteredData, tracking_id: trackId });
          if (response.status === 200) {
            setTimeout(() => {
              setLoading(false);
              setEditingStep(null);
            }, 1000); 
            fetchTrackingData();   
          }
        }
      } catch (error) {
        console.error("Submission error:", error);
        alert("An error occurred. Please try again.");
      }
    };

  return (
    <div className='tracking-container'>
    <h4 style={{fontWeight: '700'}}>Trazabilidad</h4>
    {tracking?.status === 'Cancelado' ? (
      <div
        style={{ 
            textAlign: 'center', 
            padding: '15px', 
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '5px',
            color: '#721c24',
            fontSize: '16px',
            margin: '10px auto',
            width: '80%',
            marginLeft: 'auto',
            marginRight: 'auto',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            transition: 'background-color 0.3s ease, border-color 0.3s ease'
        }}
        >
        <p>La trazabilidad de esta cotización ha sido cancelada.</p>
        </div>
    ) : (
      <div>
      <div className='delay-warning' style={{backgroundColor: getDeadlineStatus(tracking.deadline_status).backgroundColor}}>
      {tracking?.deadline_status && (
        <>
          {getDeadlineStatus(tracking.deadline_status).icon}
          <span style={{ color: getDeadlineStatus(tracking.deadline_status).color }}>
            {tracking.deadline_status}
            {tracking.deadline_detail === null ? '' : `: ${tracking.deadline_detail}`}
          </span>
        </>
        )}
      </div>

      <Tab.Container 
        id="left-tabs-example" 
        activeKey={activeKey} 
        onSelect={(key) => {
          setActiveKey(key);
          setCurrentStep(stepMap[key]);
        }}
      >
      <Row>
          <Col sm={6}>
          <Nav variant="pills" className="flex-column">
              <Nav.Item>
                <Nav.Link eventKey="Pendiente">Asignado</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="Asignado" disabled={!isStepCompleted(2)}>Contrato Firmado</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="Firmado" disabled={!isStepCompleted(3)}>Confirmación</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="Confirmado" disabled={!isStepCompleted(4)}>Recepción de BL</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="BL recibido" disabled={!isStepCompleted(5)}>Aprobación de Documentos</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="Documentos aprobados" disabled={!isStepCompleted(6)}>Entrega de Originales</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="final" disabled={!isStepCompleted(7)}>Cerrado</Nav.Link>
              </Nav.Item>
          </Nav>
          </Col>

          <Col sm={6}>
          <Tab.Content>
              <Tab.Pane eventKey="Pendiente">
                <div className='tracking-details'>
                  <h5 style={{fontWeight: '700'}}>Asignado</h5>
                  {editingStep === "Pendiente" ? (
                    <Form onSubmit={handleSubmit(onSubmit)}>
                      <Form.Group controlId='supplier_consecutive'>
                        <Form.Label>Consecutivo del proveedor</Form.Label>
                        <Form.Control type="text" name="supplier_consecutive" {...register('supplier_consecutive')} isInvalid={!!errors.supplier_consecutive}/>
                        <Form.Control.Feedback type="invalid">
                          {errors.supplier_consecutive?.message}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group controlId='freight_type'>
                        <Form.Label>Tipo de carga</Form.Label>
                        <Form.Select type="text" name="freight_type" {...register('freight_type')} isInvalid={!!errors.freight_type}>
                          <option value="Marítimo">Marítimo</option>
                          <option value="Aereo">Aereo</option>
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.freight_type?.message}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Button
                        variant="secondary"
                        type="Submit"
                        style={{
                          width: '20%',
                          backgroundColor: '#056664',
                          border: ' solid 2px #025C5A',
                        }}
                      >
                        {loading ? 'Cargando...' : 'Guardar'}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleCancel}
                        style={{
                          width: '20%',
                          backgroundColor: '#056664',
                          border: ' solid 2px #025C5A',
                        }}
                      >
                        Cancelar
                      </Button>
                    </Form>
                  ) : (
                  <>  
                    <p><strong>Consecutivo del Proveedor: </strong> {tracking?.supplier_consecutive == null ? 'Dato no proporcionado' : tracking.supplier_consecutive} </p>
                    <p><strong>Tipo de flete: </strong> {tracking?.freight_type === '' ? 'Dato no proporcionado' : tracking.freight_type}</p>
                  </>
                  )}
                </div>
                {editingStep !== "Pendiente" && (
                <PermissionWrapper feature="trazabilidad" action="update">
                  <Button
                    variant="primary"
                    style={{ 
                        width: '20%',
                        backgroundColor: '#056664',
                        border: ' solid 2px #025C5A',
                    }} 
                    onClick={() => handleEdit("Pendiente")} 
                  >
                    Editar
                  </Button>
                </PermissionWrapper>
                )}
              </Tab.Pane>

              <Tab.Pane eventKey="Asignado">
                <div className='tracking-details'>
                <h5 style={{fontWeight: '700'}}>Contrato Firmado</h5>
                {editingStep === "Asignado" ? (
                  <Form onSubmit={handleSubmit(onSubmit)}>
                  <Form.Group controlId='initial_range_etd'>
                    <Form.Label>Rango Inicial de ETD</Form.Label>
                    <Form.Control 
                      type="date" 
                      name="initial_range_etd" 
                      {...register('initial_range_etd')} 
                      isInvalid={!!errors.initial_range_etd}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.initial_range_etd?.message}
                  </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group controlId='final_range_etd'>
                    <Form.Label>Rango Final de ETD</Form.Label>
                    <Form.Control 
                      type="date" 
                      name="final_range_etd" 
                      {...register('final_range_etd')} 
                      isInvalid={!!errors.final_range_etd}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.final_range_etd?.message}
                  </Form.Control.Feedback>
                  </Form.Group>

                  <Button
                    variant="secondary"
                    type="Submit"
                    style={{
                      width: '20%',
                      backgroundColor: '#056664',
                      border: ' solid 2px #025C5A',
                    }}
                  >
                    {loading ? 'Cargando...' : 'Guardar'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                    style={{
                      width: '20%',
                      backgroundColor: '#056664',
                      border: ' solid 2px #025C5A',
                    }}
                  >
                    Cancelar
                  </Button>
                </Form>
                ) : (
                <>
                  <p><strong>Rango inicial ETD: </strong> {tracking?.initial_range_etd == null ? 'Dato no proporcionado' : tracking.initial_range_etd}</p>
                  <p><strong>Rango final ETD: </strong>{tracking?.final_range_etd == null ? 'Dato no proporcionado' : tracking.final_range_etd}</p>
                </>
                  )}
                </div>
                {editingStep !== "Asignado" && (
                <PermissionWrapper feature="trazabilidad" action="update">
                  <Button
                    variant="primary"
                    style={{ 
                        width: '20%',
                        backgroundColor: '#056664',
                        border: ' solid 2px #025C5A',
                    }}
                    onClick={() => handleEdit("Asignado")} 
                  >
                    Editar
                  </Button>
                </PermissionWrapper>
                )}
              </Tab.Pane>

              <Tab.Pane eventKey="Firmado">
                <div className='tracking-details'>
                <h5 style={{fontWeight: '700'}}>Confirmación</h5>
                {editingStep === "Firmado" ? (
                  <Form onSubmit={handleSubmit(onSubmit)}>
                  <Form.Group controlId='confirmation_etd'>
                    <Form.Label>Confirmación ETD</Form.Label>
                    <Form.Control 
                      type="date" 
                      name="confirmation_etd" 
                      {...register('confirmation_etd')} 
                      isInvalid={!!errors.confirmation_etd}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.confirmation_etd?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group controlId='carrier_name'>
                    <Form.Label>Nombre de transportador</Form.Label>
                    <Form.Control type="text" name="carrier_name" {...register('carrier_name')} isInvalid={!!errors.carrier_name} />
                    <Form.Control.Feedback type="invalid">
                      {errors.carrier_name?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Button
                    variant="secondary"
                    type="Submit"
                    style={{
                      width: '20%',
                      backgroundColor: '#056664',
                      border: ' solid 2px #025C5A',
                    }}
                  >
                    {loading ? 'Cargando...' : 'Guardar'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                    style={{
                      width: '20%',
                      backgroundColor: '#056664',
                      border: ' solid 2px #025C5A',
                    }}
                  >
                    Cancelar
                  </Button>
                  </Form>
                ) : (
                <>
                  <p><strong>ETD de confirmación: </strong>{tracking?.confirmation_etd == null ? 'Dato no proporcionado' : tracking.confirmation_etd}</p>
                  <p><strong>Nombre de carrier: </strong>{tracking?.carrier_name == null ? 'Dato no proporcionado' : tracking.carrier_name}</p>
                </>
                  )}
                </div>
                {editingStep !== "Firmado" && (
                  <PermissionWrapper feature="trazabilidad" action="update">
                <Button
                  variant="primary"
                  style={{ 
                      width: '20%',
                      backgroundColor: '#056664',
                      border: ' solid 2px #025C5A',
                  }}
                  onClick={() => handleEdit("Firmado")} 
                  
                >
                  Editar
                </Button>
                </PermissionWrapper>
                )}
              </Tab.Pane>

              <Tab.Pane eventKey="Confirmado">
                <div className='tracking-details'>
                  <h5 style={{fontWeight: '700'}}>Recepción de BL o Guía Aérea</h5>
                  {editingStep === "Confirmado" ? (
                  <Form onSubmit={handleSubmit(onSubmit)}>
                  <Form.Group controlId='bl_number'>
                    <Form.Label>Número BL o Guía Aérea</Form.Label>
                    <Form.Control type="text" name="bl_number" {...register('bl_number')} isInvalid={!!errors.bl_number} />
                    <Form.Control.Feedback type="invalid">
                      {errors.bl_number?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group controlId='bl_date'>
                    <Form.Label>Fecha BL o Guía Aérea</Form.Label>
                    <Form.Control 
                      type="date" 
                      name="bl_date" 
                      {...register('bl_date')} 
                      isInvalid={!!errors.bl_date}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.bl_date?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group controlId='eta'>
                    <Form.Label>ETA</Form.Label>
                    <Form.Control 
                      type="date" 
                      name="eta" 
                      {...register('eta')} 
                      isInvalid={!!errors.eta}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.eta?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Button
                    variant="secondary"
                    type="Submit"
                    style={{
                      width: '20%',
                      backgroundColor: '#056664',
                      border: ' solid 2px #025C5A',
                    }}
                  >
                    {loading ? 'Cargando...' : 'Guardar'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                    style={{
                      width: '20%',
                      backgroundColor: '#056664',
                      border: ' solid 2px #025C5A',
                    }}
                  >
                    Cancelar
                  </Button>
                </Form>
                ) : (
                <>
                  <p><strong>Número de BL o Guía Aérea: </strong>{tracking?.bl_number == null ? 'Dato no proporcionado' : tracking.bl_number}</p>
                  <p><strong>Fecha de BL o Guía Aérea: </strong>{tracking?.bl_date == null ? 'Dato no proporcionado' : tracking.bl_date}</p>
                  <p><strong>ETA: </strong>{tracking?.eta == null ? 'Dato no proporcionado' : tracking.eta}</p>
                </>
                  )}
                </div>
                {editingStep !== "Confirmado" && (
                  <PermissionWrapper feature="trazabilidad" action="update">
                <Button
                  variant="primary"
                  style={{ 
                      width: '20%',
                      backgroundColor: '#056664',
                      border: ' solid 2px #025C5A',
                  }}
                  onClick={() => handleEdit("Confirmado")} 
                  
                >
                  Editar
                </Button>
                </PermissionWrapper>
                )}
              </Tab.Pane>

              <Tab.Pane eventKey="BL recibido">
                <div className='tracking-details'>
                  <h5 style={{fontWeight: '700'}}>Aprobación de Documentos</h5>
                  {editingStep === "BL recibido" ? (
                  <Form onSubmit={handleSubmit(onSubmit)}>
                  {['checkbox'].map((type) => (
                  <div key={`inline-${type}`} className="mb-3">
                    <Form.Group controlId="approved_documents">
                    <Form.Check
                      inline
                      label="Los documentos han sido aprobados"
                      name="group1"
                      type={type}
                      id={`inline-${type}-1`}
                      {...register('approved_documents')}
                    />
                    </Form.Group>
                  </div>
                  ))}
                  <Button
                    variant="secondary"
                    type="Submit"
                    style={{
                      width: '20%',
                      backgroundColor: '#056664',
                      border: ' solid 2px #025C5A',
                    }}
                  >
                    {loading ? 'Cargando...' : 'Guardar'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                    style={{
                      width: '20%',
                      backgroundColor: '#056664',
                      border: ' solid 2px #025C5A',
                    }}
                  >
                    Cancelar
                  </Button>
                  </Form>
                ) : (
                <>
                  <p>
                    {tracking?.approved_documents ? (
                      <>Los documentos se encuentrán <strong>aprobados</strong></>
                    ) : (
                      <>Los documentos <strong>no han sido aprobados</strong></>
                    )}
                  </p>
                </>
                  )}
                </div>
                {editingStep !== "BL recibido" && (
                  <PermissionWrapper feature="trazabilidad" action="update">
                <Button
                  variant="primary"
                  style={{ 
                      width: '20%',
                      backgroundColor: '#056664',
                      border: ' solid 2px #025C5A',
                  }}
                  onClick={() => handleEdit("BL recibido")} 
                  
                >
                  Editar
                </Button>
                </PermissionWrapper>
                )}
              </Tab.Pane>

              <Tab.Pane eventKey="Documentos aprobados">
                <div className='tracking-details'>
                  <h5 style={{fontWeight: '700'}}>Entrega de Originales</h5>
                  {editingStep === "Documentos aprobados" ? (
                  <Form onSubmit={handleSubmit(onSubmit)}>
                  <Form.Group controlId='container_days'>
                    <Form.Label>Días de contenedor</Form.Label>
                    <Form.Control type="number" name="container_days" {...register('container_days')} isInvalid={!!errors.container_days}/>
                    <Form.Control.Feedback type="invalid">
                      {errors.container_days?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group controlId='original_documents_date'>
                    <Form.Label>Fecha de entrega de documentos originales</Form.Label>
                    <Form.Control 
                      type="date" 
                      name="original_documents_date" 
                      {...register('original_documents_date')} 
                      isInvalid={!!errors.original_documents_date}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.original_documents_date?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {['checkbox'].map((type) => (
                  <div key={`inline-${type}`} className="mb-3">
                    <Form.Group controlId="bl_in_destination">
                    <Form.Check
                      inline
                      label="El BL se encuentra en destino"
                      name="group1"
                      type={type}
                      id={`inline-${type}-1`}
                      {...register('bl_in_destination')}
                    />
                    </Form.Group>
                  </div>
                  ))}

                  <Button
                    variant="secondary"
                    type="Submit"
                    style={{
                      width: '20%',
                      backgroundColor: '#056664',
                      border: ' solid 2px #025C5A',
                    }}
                  >
                    {loading ? 'Cargando...' : 'Guardar'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                    style={{
                      width: '20%',
                      backgroundColor: '#056664',
                      border: ' solid 2px #025C5A',
                    }}
                  >
                    Cancelar
                  </Button>
                  </Form>
                ) : (
                <>
                  <p><strong>Días de contenedor: </strong>{tracking?.container_days == null ? 'Dato no proporcionado' : tracking.container_days}</p>
                  <p style={{marginBottom: '5px'}}><strong>Fecha de entrega de documentos originales: </strong></p>
                  <p style={{marginTop: '0px'}}>{tracking?.original_documents_date == null ? 'Dato no proporcionado' : tracking.original_documents_date}</p>
                  <p>
                    {tracking?.bl_in_destination ? (
                      <>El BL se encuentra en el destino</>
                    ) : (
                      <>El BL se entrega por correo</>
                    )}
                  </p>
                </>
                  )}
                </div>
                {editingStep !== "Documentos aprobados" && (
                  <PermissionWrapper feature="trazabilidad" action="update">
                <Button
                  variant="primary"
                  style={{ 
                      width: '20%',
                      backgroundColor: '#056664',
                      border: ' solid 2px #025C5A',
                  }}
                  onClick={() => handleEdit("Documentos aprobados")} 
                  
                >
                  Editar
                </Button>
                </PermissionWrapper>
                )}
              </Tab.Pane>

              <Tab.Pane eventKey="final">
                <div className='tracking-details'>
                <h5 style={{fontWeight: '700'}}>Fin de Trazabilidad</h5>
                {editingStep === "final" ? (
                  <Form onSubmit={handleSubmit(onSubmit)}>
                  <Form.Group controlId='ata'>
                    <Form.Label>ATA</Form.Label>
                    <Form.Control 
                      type="date" 
                      name="confirmation_etd" 
                      {...register('ata')} 
                      isInvalid={!!errors.ata}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.ata?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Button
                    variant="secondary"
                    type="Submit"
                    style={{
                      width: '20%',
                      backgroundColor: '#056664',
                      border: ' solid 2px #025C5A',
                    }}
                  >
                    {loading ? 'Cargando...' : 'Guardar'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCancel}
                    style={{
                      width: '20%',
                      backgroundColor: '#056664',
                      border: ' solid 2px #025C5A',
                    }}
                  >
                    Cancelar
                  </Button>
                  </Form>
                ) : (
                <>
                  <p><strong>ATA: </strong>{tracking?.ata == null ? 'Dato no proporcionado' : tracking.ata}</p>
                </>
                  )}
                </div>
                {editingStep !== "final" && (
                  <PermissionWrapper feature="trazabilidad" action="update">
                <Button
                  variant="primary"
                  style={{ 
                      width: '20%',
                      backgroundColor: '#056664',
                      border: ' solid 2px #025C5A',
                  }}
                  onClick={() => handleEdit("final")} 
                  
                >
                  Editar
                </Button>
                </PermissionWrapper>  
                )}
              </Tab.Pane>
          </Tab.Content>
          </Col>
      </Row>
      </Tab.Container>
      </div>
      )}
    </div>
  )
}

export default OrderTracking